import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import {
    Trip,
    CarOption,
    ApiResponse,
    DateType,
    SearchParams
} from '../../components/layout/reports/Reports.types';

interface ReportsState {
    loading: boolean;
    error: string | null;
    trips: Trip[];
    tripTypes: any[]; // Define a proper type based on your API response
    cars: CarOption[];
    dateType: DateType;
    fromDate: string | null; // Store as ISO string for serialization
    toDate: string | null;
    carTypeId: number;
    selectedStatuses: string[];
    selectedTripTypes: string[];
    selectedLeadSources: string[];
    initialized: boolean;
}

const initialState: ReportsState = {
    loading: false,
    error: null,
    trips: [],
    tripTypes: [],
    cars: [],
    dateType: 'PICKUP',
    fromDate: (() => {
        const date = new Date();
        date.setDate(date.getDate() - 7);
        return date.toISOString();
    })(),
    toDate: new Date().toISOString(),
    carTypeId: 1,
    selectedStatuses: ['CONFIRMED'],
    selectedTripTypes: [],
    // selectedLeadSources: [],
    selectedLeadSources: [],  // All selected by default
    initialized: false
};

// Async thunks
export const fetchCars = createAsyncThunk(
    'reports/fetchCars',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.getAllCars();
            const carsData: CarOption[] = response?.data?.data || [];
            return carsData;
        } catch (error) {
            console.error('Error fetching cars:', error);
            return rejectWithValue('Failed to fetch cars');
        }
    }
);

export const searchTrips = createAsyncThunk(
    'reports/searchTrips',
    async (searchParams: SearchParams, { rejectWithValue }) => {
        try {
            const response = await api.searchTrips(searchParams) as ApiResponse<Trip[]>;
            const tripsData = response?.data || [];

            if (response?.message === 'Success') {
                return tripsData;
            } else {
                console.warn("Response did not return status=true:", response);
                return [];
            }
        } catch (error) {
            console.error("Error searching trips:", error);
            return rejectWithValue('Failed to fetch trip data');
        }
    }
);

export const fetchTripTypes = createAsyncThunk(
    'reports/fetchTripTypes',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.fetchTripTypes() as ApiResponse<any[]>;
            const tripTypesData = response?.data || [];
            return tripTypesData;
        } catch (error) {
            console.error("Error fetching trip types:", error);
            return rejectWithValue('Failed to fetch trip types');
        }
    }
);

const reportsSlice = createSlice({
    name: 'reports',
    initialState,
    reducers: {
        setDateType: (state, action: PayloadAction<DateType>) => {
            state.dateType = action.payload;
        },
        setFromDate: (state, action: PayloadAction<string | null>) => {
            state.fromDate = action.payload;
        },
        setToDate: (state, action: PayloadAction<string | null>) => {
            state.toDate = action.payload;
        },
        setCarTypeId: (state, action: PayloadAction<number>) => {
            state.carTypeId = action.payload;
        },
        setSelectedStatuses: (state, action: PayloadAction<string[]>) => {
            state.selectedStatuses = action.payload;
        },
        setSelectedTripTypes: (state, action: PayloadAction<string[]>) => {
            state.selectedTripTypes = action.payload;
        },
        setSelectedLeadSources: (state, action: PayloadAction<string[]>) => {
            state.selectedLeadSources = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        setInitialized: (state, action: PayloadAction<boolean>) => {
            state.initialized = action.payload;
        },
        resetReportsState: (state) => {
            // Reset to initial state but keep cars data to avoid refetching
            const carsData = state.cars;
            const tripTypesData = state.tripTypes;
            Object.assign(state, {
                ...initialState,
                cars: carsData,
                tripTypes: tripTypesData,
                selectedTripTypes: state.selectedTripTypes,
                selectedLeadSources: state.selectedLeadSources,
                initialized: (carsData.length > 0 && tripTypesData.length > 0)
            });
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch cars
            .addCase(fetchCars.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCars.fulfilled, (state, action) => {
                state.loading = false;
                state.cars = action.payload;
                state.error = null;
            })
            .addCase(fetchCars.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.cars = [];
            })
            // Search trips
            .addCase(searchTrips.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(searchTrips.fulfilled, (state, action) => {
                state.loading = false;
                state.trips = action.payload;
                state.error = null;
            })
            .addCase(searchTrips.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.trips = [];
            })
            .addCase(fetchTripTypes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTripTypes.fulfilled, (state, action) => {
                state.loading = false;
                state.tripTypes = action.payload;
                // Auto-select all trip types on initial load
                if (state.selectedTripTypes.length === 0) {
                    state.selectedTripTypes = action.payload
                        .map((tt: any) => tt.id?.toString());
                }
                state.error = null;
            })
            .addCase(fetchTripTypes.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.tripTypes = [];
            });
    }
});

export const {
    setDateType,
    setFromDate,
    setToDate,
    setCarTypeId,
    setSelectedStatuses,
    setSelectedTripTypes,
    setSelectedLeadSources,
    setError,
    clearError,
    setInitialized,
    resetReportsState
} = reportsSlice.actions;

export default reportsSlice.reducer;
